import React, { useEffect } from 'react';
import styles from './input.module.scss';
import clsx from 'clsx';
import {
  ArrowPressHandler,
  navigateByDirection,
  pause,
  resume,
  useFocusable
} from '@noriginmedia/norigin-spatial-navigation';
import useEventListener from '@smart-tv/hooks/use-event-listener';
import {
  isBackKey,
  isDownKey,
  isImeCancelKey,
  isImeDoneKey,
  isLeftKey,
  isRightKey,
  isUpKey
} from '@smart-tv/utils/remoteKeys';

export interface Props {
  className?: string;
  focusKey?: string;
  onArrowPress?: ArrowPressHandler<{ focusKey: string | undefined }>;
  name?: string;
  type?: 'text' | 'password' | 'email' | 'submit' | 'search';
  placeholder?: string;
  maxLength?: number;
  value: string;
  onChange?: (event: React.ChangeEvent<HTMLInputElement>) => void;
  forceKeyboard?: boolean;
  autocomplete?: string;
  inputProps?: any;
}
export const Input = (props: Props) => {
  const {
    focusKey: focusKeyProp,
    onArrowPress,
    name,
    type = 'text',
    value,
    placeholder,
    maxLength,
    onChange,
    forceKeyboard = false,
    autocomplete = 'off',
    inputProps = {},
    className,
    ...rest
  } = props;

  const [animating, setAnimating] = React.useState(false);

  const onEnterPress = () => {
    setAnimating(true);
    ref.current.focus();
  };

  const { ref, focused } = useFocusable({ onEnterPress, onArrowPress, focusKey: focusKeyProp });

  // Extract non-DOM props and caller className from inputProps so they don't
  // leak to the DOM or silently override the computed class list.
  const { error, className: inputPropsClassName, ...safeInputProps } = inputProps;

  // Mirrors the library's own paused flag (it has no public getter) so we know whether its
  // keydown listener - which runs before this one and bails out early while paused - actually
  // saw this same keypress or silently dropped it.
  const isPausedRef = React.useRef(false);

  const handleKeyDown = React.useCallback(
    (event: KeyboardEvent) => {
      if (document.activeElement === ref.current) {
        if (isImeDoneKey(event) || isImeCancelKey(event) || isDownKey(event) || isUpKey(event)) {
          const wasPaused = isPausedRef.current;
          isPausedRef.current = false;
          ref.current.blur();
          resume();

          // The library's listener fires before this one, so if we were still paused it
          // already ignored this keypress - manually perform the move it missed.
          if (wasPaused && isUpKey(event)) navigateByDirection('up', {});
          if (wasPaused && isDownKey(event)) navigateByDirection('down', {});

          return;
        }

        if (isLeftKey(event) || isRightKey(event)) {
          const input = ref.current;
          const atStart = input.selectionStart === 0 && input.selectionEnd === 0;
          const atEnd =
            input.selectionStart === input.value.length &&
            input.selectionEnd === input.value.length;

          if ((isLeftKey(event) && atStart) || (isRightKey(event) && atEnd)) {
            const wasPaused = isPausedRef.current;
            isPausedRef.current = false;
            input.blur();
            resume();

            if (wasPaused) navigateByDirection(isLeftKey(event) ? 'left' : 'right', {});

            return;
          }
        }

        isPausedRef.current = true;
        pause();
      }
    },
    [ref]
  );

  const handleBackKey = React.useCallback(
    (event: KeyboardEvent) => {
      if (document.activeElement === ref.current && isBackKey(event)) {
        event.stopPropagation();
        ref.current.blur();
        isPausedRef.current = false;
        resume();
      }
    },
    [ref]
  );

  useEffect(() => {
    if (forceKeyboard) {
      ref.current.focus();
    }
  }, [forceKeyboard, ref]);

  useEffect(() => {
    const handleWebOsKeyboardStateChange = (event: Event) => {
      const { detail } = event as CustomEvent<{ visibility: boolean }>;
      if (detail?.visibility === false && document.activeElement === ref.current) {
        ref.current.blur();
        isPausedRef.current = false;
        resume();
      }
    };

    document.addEventListener('keyboardStateChange', handleWebOsKeyboardStateChange);
    return () =>
      document.removeEventListener('keyboardStateChange', handleWebOsKeyboardStateChange);
  }, [ref]);

  useEventListener('keydown', (event: KeyboardEvent) => handleKeyDown(event));
  useEventListener('keydown', handleBackKey, undefined, true);

  return (
    <input
      ref={ref}
      name={name}
      type={type}
      value={value}
      onChange={onChange}
      placeholder={placeholder}
      maxLength={maxLength}
      autoComplete={autocomplete}
      onAnimationEnd={() => setAnimating(false)}
      {...safeInputProps}
      {...rest}
      className={clsx(className, inputPropsClassName, styles['input'], {
        [styles['input--focused']]: focused,
        [styles['input--error']]: error,
        [styles['input--animating']]: animating
      })}
    />
  );
};

export default Input;
